import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import './LoginWindow.css';
import API_BASE from '../../../apiConfig';

const ROLE_OPTIONS = [
  { value: 'demand_planner', label: 'Demand Planner' },
  { value: 'brand_manager', label: 'Brand Manager' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'sc_analyst', label: 'SC Analyst' },
  { value: 'executive', label: 'Executive' },
  { value: 'admin', label: 'Admin' }
];

const LoginWindow = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('demand_planner');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, role: selectedRole }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid username or password');
      }

      login(data.access_token, data.user || { username, role: selectedRole });
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-split-container">
        
        {/* Left Side: Decorative Art/Branding */}
        <div className="login-hero-section">
          <div className="login-hero-content">
            <div className="logo-badge">ForeC.AI</div>
            <h2>Next Generation <br/>Supply Chain Intelligence</h2>
            <p>Empowering your organization with prescriptive analytics, scenario modeling, and predictive insights.</p>
            
            <div className="hero-floating-elements">
              <div className="float-card card-1">
                <span>Forecast Accuracy</span>
                <strong>+24%</strong>
              </div>
              <div className="float-card card-2">
                <span>Optimization</span>
                <strong>Real-time</strong>
              </div>
            </div>
          </div>
          <div className="hero-overlay"></div>
        </div>

        {/* Right Side: Login Form */}
        <div className="login-form-section">
          <div className="login-form-container">
            <div className="login-header">
              <h1 className="login-title">Welcome Back</h1>
              <p className="login-subtitle">Sign in to the Organization Dashboard</p>
            </div>
            
            {error && (
              <div className="login-error-alert animate-shake">
                <span className="alert-icon">!</span>
                <span className="alert-message">{error}</span>
              </div>
            )}

            <form className="login-form" onSubmit={handleLogin}>
              <div className="input-group">
                <label htmlFor="username">Username</label>
                <div className="input-with-icon">
                  <span className="input-icon">👤</span>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="e.g. admin"
                    autoComplete="username"
                    required
                  />
                </div>
              </div>
              
              <div className="input-group">
                <label htmlFor="password">Password</label>
                <div className="input-with-icon">
                  <span className="input-icon">🔒</span>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                  />
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="role">Select Role</label>
                <div className="input-with-icon">
                  <span className="input-icon">🛡️</span>
                  <select
                    id="role"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem 0.875rem 2.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      color: '#111827',
                      background: '#f9fafb',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                      cursor: 'pointer'
                    }}
                  >
                    {ROLE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-button">Forgot password?</Link>
              </div>

              <button 
                type="submit" 
                className={`btn-primary login-btn ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner"></span>
                ) : (
                  <>
                    <span>Sign In</span>
                    <span className="btn-icon">→</span>
                  </>
                )}
              </button>
            </form>

            <div className="auth-footer-link">
              Don't have an account? <Link to="/register">Register here</Link>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginWindow;
